import type { CollectedData, RecommendationResult } from '../../context/RecommendationContext';
import type { SystemRecInput } from '@core/types';
import { toOptionChartRows } from '../../lib/optionCharts/optionChartModel';
import { buildDecisionPack, type DecisionPackData } from '@core/presentationPrint';
import {
  createInputFingerprint,
  createOutputFingerprint,
  getEnvironment,
  getSessionId
} from './debugBundleHelpers';

export interface DebugBundleMeta {
  createdAt: string;
  route: string;
  appVersion: string;
  env: string;
  userAgent: string;
  timezone: string;
  sessionId: string;
  inputFingerprint?: string;
  outputFingerprint?: string;
  trimmedFields?: string[];
}

export interface DebugBundleEvidence {
  appliedInputs?: Record<string, any>;
  missingInputs?: string[];
  fallbacksUsed?: Array<{ field: string; reason: string; value: any }>;
  rejections?: Array<{ optionId: string; reason: string; detail?: string }>;
  droppedFields?: Array<{ field: string; rawValue: any; reason: string }>;  // Fields lost during normalization
}

export interface DebugBundlePDF {
  included: boolean;
  generatedFrom: string;
  pdfUrl?: string;
  sha256?: string;
  base64?: string | null;
}

export interface DebugBundle {
  meta: DebugBundleMeta;
  notes: string;
  rawInput: CollectedData | null;
  normalizedInput: SystemRecInput | null;
  computeOutput: any;
  evidence: DebugBundleEvidence;
  pdf: DebugBundlePDF;
  chartData?: {
    rows: any[];
    metadata: {
      generatedAt: string;
      totalOptions: number;
      excludedCount: number;
      recommendedCount: number;
    };
  };
  decisionPack?: DecisionPackData;
}

/**
 * Detect fields that were dropped during normalization
 * Compares rawInput to normalizedInput to find missing critical fields
 */
function detectDroppedFields(
  rawInput: CollectedData | null,
  normalizedInput: SystemRecInput | null
): Array<{ field: string; rawValue: any; reason: string }> {
  if (!rawInput || !normalizedInput) {
    return [];
  }

  const droppedFields: Array<{ field: string; rawValue: any; reason: string }> = [];

  // Check GC number
  if (rawInput.gcNumber && rawInput.gcNumber !== 'unknown' && rawInput.gcNumber.trim() !== '') {
    const hasGCInNormalized = normalizedInput.currentSystemEfficiency?.gcNumber;
    if (!hasGCInNormalized) {
      droppedFields.push({
        field: 'gcNumber',
        rawValue: rawInput.gcNumber,
        reason: 'GC number provided in raw input but not present in normalized input'
      });
    }
  }

  // Check boiler make/model
  if (rawInput.boilerMake && rawInput.boilerMake !== 'unknown' && rawInput.boilerMake.trim() !== '') {
    const hasMakeInNormalized = normalizedInput.currentSystemEfficiency?.boilerMake;
    if (!hasMakeInNormalized) {
      droppedFields.push({
        field: 'boilerMake',
        rawValue: rawInput.boilerMake,
        reason: 'Boiler make provided in raw input but not present in normalized input'
      });
    }
  }

  if (rawInput.boilerModel && rawInput.boilerModel !== 'unknown' && rawInput.boilerModel.trim() !== '') {
    const hasModelInNormalized = normalizedInput.currentSystemEfficiency?.boilerModel;
    if (!hasModelInNormalized) {
      droppedFields.push({
        field: 'boilerModel',
        rawValue: rawInput.boilerModel,
        reason: 'Boiler model provided in raw input but not present in normalized input'
      });
    }
  }

  // Check solar panels
  if (rawInput.solarPanels && rawInput.solarPanels !== 'unknown') {
    const hasSolarInNormalized = normalizedInput.solarPanels;
    if (!hasSolarInNormalized) {
      droppedFields.push({
        field: 'solarPanels',
        rawValue: rawInput.solarPanels,
        reason: 'Solar panels provided in raw input but not present in normalized input'
      });
    }
  }

  // Check propertyAgeBand (new check to detect corruption)
  if (rawInput.propertyAgeBand && rawInput.propertyAgeBand !== 'unknown') {
    if (normalizedInput.propertyAgeBand !== rawInput.propertyAgeBand) {
      droppedFields.push({
        field: 'propertyAgeBand',
        rawValue: rawInput.propertyAgeBand,
        reason: `Property age band changed from "${rawInput.propertyAgeBand}" to "${normalizedInput.propertyAgeBand}" during normalization`
      });
    }
  }

  // Check flowRate (new check to detect override)
  if (rawInput.flowRate && rawInput.flowRate !== 'unknown' && rawInput.flowRate.trim() !== '') {
    const rawFlowRate = parseFloat(rawInput.flowRate);
    if (!isNaN(rawFlowRate) && rawFlowRate > 0) {
      if (!normalizedInput.flowRate || Math.abs(normalizedInput.flowRate - rawFlowRate) > 0.1) {
        droppedFields.push({
          field: 'flowRate',
          rawValue: rawInput.flowRate,
          reason: `Flow rate changed from ${rawInput.flowRate} L/min to ${normalizedInput.flowRate || 'undefined'} during normalization`
        });
      }
    }
  }

  // Check sludgeRisk (new check to detect wiping)
  if (rawInput.sludgeRisk && rawInput.sludgeRisk !== 'none' && rawInput.sludgeRisk !== 'unknown') {
    const expectedSludgeRisk = (rawInput.sludgeRisk || '').toUpperCase();
    if (normalizedInput.sludgeRisk !== expectedSludgeRisk) {
      droppedFields.push({
        field: 'sludgeRisk',
        rawValue: rawInput.sludgeRisk,
        reason: `Sludge risk changed from "${rawInput.sludgeRisk}" to "${normalizedInput.sludgeRisk}" during normalization`
      });
    }
  }

  // Check SEDBUK band (new check to detect baseline enablement issues)
  if (rawInput.sedbukBand && rawInput.sedbukBand !== 'unknown') {
    const hasSedbukInNormalized = normalizedInput.currentSystemEfficiency?.sedbukBand;
    if (!hasSedbukInNormalized) {
      droppedFields.push({
        field: 'sedbukBand',
        rawValue: rawInput.sedbukBand,
        reason: 'SEDBUK band provided in raw input but not present in normalized input'
      });
    }
    
    // Check if baseline is enabled when SEDBUK data exists
    if (hasSedbukInNormalized && !normalizedInput.baselineEnabled) {
      droppedFields.push({
        field: 'baselineEnabled',
        rawValue: 'should be true',
        reason: 'SEDBUK band is present but baselineEnabled is false'
      });
    }
    
    // Check if source is correct
    if (hasSedbukInNormalized && normalizedInput.currentSystemEfficiency?.source === 'ASSUMED') {
      droppedFields.push({
        field: 'currentSystemEfficiency.source',
        rawValue: 'should be SEDBUK',
        reason: 'SEDBUK band is present but source is set to ASSUMED'
      });
    }
  }

  return droppedFields;
}

/**
 * Extract evidence from recommendation result
 */
function extractEvidence(result: RecommendationResult | null): DebugBundleEvidence {
  if (!result) {
    return {
      appliedInputs: {},
      missingInputs: [],
      fallbacksUsed: [],
      rejections: [],
      droppedFields: []
    };
  }

  // Extract evidence from compute output if available
  const output = result.output;
  
  if (output && 'evidence' in output && output.evidence) {
    const computeEvidence = output.evidence as any;
    return {
      appliedInputs: computeEvidence.appliedInputs || {},
      missingInputs: computeEvidence.missingInputs || [],
      fallbacksUsed: computeEvidence.fallbacksUsed || [],
      rejections: computeEvidence.rejections || [],
      droppedFields: [] // Will be populated by dropped field detection
    };
  }
  
  // Fallback: try to extract evidence from legacy format
  const evidence: DebugBundleEvidence = {
    appliedInputs: {},
    missingInputs: [],
    fallbacksUsed: [],
    rejections: [],
    droppedFields: []
  };
  
  // Look for rejected options (legacy format)
  if (output && 'rejected' in output) {
    const rejected = (output as unknown as { rejected?: Array<{ id?: string; optionId?: string; reason?: string; whyNot?: string; detail?: string; details?: string }> }).rejected;
    
    if (Array.isArray(rejected)) {
      evidence.rejections = rejected.map((r) => ({
        optionId: r.id || r.optionId || 'unknown',
        reason: r.reason || r.whyNot || 'Not specified',
        detail: r.detail || r.details || undefined
      }));
    }
  }

  return evidence;
}

/**
 * Check if the JSON size exceeds the limit and trim if necessary
 */
function trimLargeFields(data: any, maxSizeBytes = 2 * 1024 * 1024): { data: any; trimmedFields: string[] } {
  const jsonString = JSON.stringify(data);
  const sizeBytes = new Blob([jsonString]).size;
  
  if (sizeBytes <= maxSizeBytes) {
    return { data, trimmedFields: [] };
  }
  
  // If too large, trim computeOutput fields but preserve critical data
  const trimmedFields: string[] = [];
  const trimmedData = { ...data };
  
  // Try removing large array fields from computeOutput
  if (trimmedData.computeOutput) {
    const output = { ...trimmedData.computeOutput };
    
    // Remove large series data if present
    if (output.yearlySeries) {
      delete output.yearlySeries;
      trimmedFields.push('computeOutput.yearlySeries');
    }
    
    if (output.timeseries) {
      delete output.timeseries;
      trimmedFields.push('computeOutput.timeseries');
    }
    
    trimmedData.computeOutput = output;
  }
  
  // If still too large, trim decisionPack (least critical for debugging)
  if (trimmedData.decisionPack) {
    const newSize = new Blob([JSON.stringify(trimmedData)]).size;
    if (newSize > maxSizeBytes) {
      delete trimmedData.decisionPack;
      trimmedFields.push('decisionPack');
    }
  }
  
  return { data: trimmedData, trimmedFields };
}

/**
 * Build a complete debug bundle from current state
 * @param rawInput - Raw collected data before normalization
 * @param normalizedInput - Normalized SystemRecInput
 * @param recommendationResult - Full recommendation result with compute output
 * @param notes - User-provided notes
 * @param route - Current route path
 * @returns Complete debug bundle ready for export
 */
export function buildDebugBundle(
  rawInput: CollectedData | null,
  normalizedInput: SystemRecInput | null,
  recommendationResult: RecommendationResult | null,
  notes: string,
  route: string
): DebugBundle {
  const computeOutput = recommendationResult?.output || null;
  const evidence = extractEvidence(recommendationResult);
  
  // Detect dropped fields
  const droppedFields = detectDroppedFields(rawInput, normalizedInput);
  evidence.droppedFields = droppedFields;
  
  // Create fingerprints
  const inputFingerprint = normalizedInput ? createInputFingerprint(normalizedInput) : undefined;
  const outputFingerprint = computeOutput ? createOutputFingerprint(computeOutput) : undefined;
  
  // Get app version (try multiple sources)
  let appVersion = 'unknown';
  try {
    // @ts-ignore - build-time injected
    appVersion = import.meta.env?.VITE_APP_VERSION || 
                 // @ts-ignore - git info
                 import.meta.env?.VITE_GIT_SHA || 
                 'dev';
  } catch {
    appVersion = 'dev';
  }
  
  const bundle: DebugBundle = {
    meta: {
      createdAt: new Date().toISOString(),
      route,
      appVersion,
      env: getEnvironment(),
      userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : 'test-environment',
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      sessionId: getSessionId(),
      inputFingerprint,
      outputFingerprint,
    },
    notes,
    rawInput,
    normalizedInput,
    computeOutput,
    evidence,
    pdf: {
      included: false,
      generatedFrom: route,
      pdfUrl: undefined,
      sha256: undefined,
      base64: null
    }
  };

  // Add chart data if output is available
  if (computeOutput) {
    try {
      const chartRows = toOptionChartRows(computeOutput);
      const recommendedOptions = computeOutput.primary 
        ? [computeOutput.primary, ...(computeOutput.alternatives || [])]
        : [];
      const excludedCount = chartRows.filter(row => row.excluded).length;

      bundle.chartData = {
        rows: chartRows,
        metadata: {
          generatedAt: new Date().toISOString(),
          totalOptions: chartRows.length,
          excludedCount,
          recommendedCount: recommendedOptions.length,
        }
      };
    } catch (error) {
      console.warn('Failed to generate chart data for debug bundle:', error);
    }
  }
  
  // Add Decision Pack data if output is available with inputEcho
  if (computeOutput && computeOutput.inputEcho) {
    try {
      const decisionPack = buildDecisionPack(computeOutput, computeOutput.inputEcho);
      bundle.decisionPack = decisionPack;
    } catch (error) {
      console.warn('Failed to generate Decision Pack for debug bundle:', error);
    }
  }
  
  // Check size and trim if necessary
  const { data, trimmedFields } = trimLargeFields(bundle);
  
  if (trimmedFields.length > 0) {
    data.meta.trimmedFields = trimmedFields;
  }
  
  return data;
}
